import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import Select from "../inputs/select";
import Toast from "../toast";
import { concurrentChangeToast } from "../toast/concurrentChange";
import Loader from "../misc/loader";
import Button from "../inputs/button";
import BasicInput from "../inputs/basic";
import ToggleSwitch from "../inputs/toggle";
import { getTag, getTagBuilderConfiguration } from "../../services/tag";
import { createCohort, updateCohort, getCohortbyCohortID, getCohortSizing, isConcurrentChange } from "../../services/cohort";
import { formatDv360SlotSuffix } from "../../utilities/dv360Slots";
import { DeleteCohort } from "../../services/delete";
import { getSession } from "../../services/auth";
import { ttlOptions } from "../../utilities/constants";
import { isReadOnly as computeReadOnly, canManageDestinations as computeCanManageDestinations, canManageLifecycleAndDelivery as computeCanManageLifecycle } from "../../utilities/permissions";
import "./AudienceBuilderV2.scss";
import "./criteria/shared.scss";
// Extracted utils
import { transformCriteriaForApi, transformCriteriaFromApi } from "./utils/criteriaTransforms";
import { isValidCriteria, isValidGroup } from "./utils/criteriaValidation";
import { inferMembershipModeFromCriteria } from "./utils/membershipModeInference";
// Criteria components
import DomainsCard from "./criteria/DomainsCard";
import TechnographicsCard from "./criteria/TechnographicsCard";
import DemographicsCard from "./criteria/DemographicsCard";
import LocationCard from "./criteria/LocationCard";
import SubCohortsCard from "./criteria/SubCohortsCard";
import RealtimeSiteContentCard from "./criteria/RealtimeSiteContentCard";
import ContentConsumptionCard from "./criteria/ContentConsumptionCard";
import SyndicatedCard from "./criteria/SyndicatedCard";
import CustomUserDataCard from "./criteria/CustomUserDataCard";
// Modals
import { DestinationsModal, ContextualSwitchModal } from "./modals";
// Page layout shared with the signal / modeled audience pages (ASB-4018)
import AudienceHeader from "../layout/audienceHeader";
import MetricsBar from "../layout/metricsBar";
import BuilderCard, { BuilderCardRemoveButton } from "../layout/builderCard";
// Assets
import resizeIcon from "../../assets/svgs/resize.svg";
import infoIcon from "../../assets/svgs/info.svg";
import chevronDownIcon from "../../assets/svgs/chevron-down.svg";
import questionIcon from "../../assets/svgs/question.svg";
import plusCircleIcon from "../../assets/svgs/plus-circle.svg";
import sparkleIcon from "../../assets/svgs/sparkle.svg";
import sparkleCircleIcon from "../../assets/svgs/sparkle-circle.svg";
import xIcon from "../../assets/svgs/x.svg";
import AIAudienceExplorerV2 from "../../views/aiAudienceExplorerV2";

/**
 * Criteria type definitions for the dropdown
 */
const CRITERIA_TYPES = {
	domains: {
		key: "domains",
		label: "Domains",
		contextualAllowed: true,
		resolution: "atomic",
	},
	contentConsumption: {
		key: "contentConsumption",
		label: "Content Consumption",
		apiKey: "siteContent",
		targetType: "behavioral",
		contextualAllowed: false,
		resolution: "granular",
	},
	realtimeSiteContent: {
		key: "realtimeSiteContent",
		label: "Realtime Site Content",
		apiKey: "siteContent",
		targetType: "contextual",
		contextualAllowed: true,
		resolution: "granular",
	},
	demographics: {
		key: "demographics",
		label: "Demographics",
		contextualAllowed: false,
		resolution: "atomic",
	},
	syndicated: {
		key: "syndicated",
		label: "Syndicated Data Segments",
		contextualAllowed: false,
		resolution: "granular",
	},
	customUserData: {
		key: "customUserData",
		label: "Custom User Data",
		contextualAllowed: false,
		resolution: "granular",
	},
	location: {
		key: "location",
		label: "Location",
		contextualAllowed: true,
		resolution: "granular",
	},
	technographics: {
		key: "technographics",
		label: "Technographics",
		contextualAllowed: true,
		resolution: "atomic",
	},
	subCohorts: {
		key: "subCohorts",
		label: "Other Cohorts",
		contextualAllowed: true,
		resolution: "granular",
	},
};

/**
 * AudienceBuilderV2 - Main container component for the new Audience Builder
 *
 * @param {string} mode - "create" | "edit" | "view"
 * @param {string} cohortId - Cohort ID for edit/view modes
 */
function AudienceBuilderV2({ mode = "create", cohortId = null, initialData = null, fromGenerationId = null, createSiloId = null }) {
	const navigate = useNavigate();

	// Core state
	const [audienceName, setAudienceName] = useState("");
	const [description, setDescription] = useState("");
	const [criteriaGroupCondition, setCriteriaGroupCondition] = useState("and");
	const [criteriaGroups, setCriteriaGroups] = useState([[{ type: null, data: {} }]]); // Start with one empty criteria card
	const [membershipModeSetting, setMembershipModeSetting] = useState("auto"); // "auto" | "transient" | "persistent"
	const [resolvedMembershipMode, setResolvedMembershipMode] = useState("transient"); // the actual mode
	const [subCohortCache, setSubCohortCache] = useState({}); // { unique_id: resolved_membership_mode }
	const [ttl, setTtl] = useState(30);
	// The version this audience was loaded at, sent back on save so a save someone else made
	// in the meantime is refused rather than overwritten (ASB-4801).
	const [loadedVersion, setLoadedVersion] = useState(null);
	const [defaultTtl, setDefaultTtl] = useState(null); // Publisher's default TTL
	const [contextualOnly, setContextualOnly] = useState(false);
	const [previousMembershipModeSetting, setPreviousMembershipModeSetting] = useState(null);
	const [dataCenter, setDataCenter] = useState("us-east-1");
	const [amsFlavor, setAmsFlavor] = useState("full");
	const [modelingEnabled, setModelingEnabled] = useState(false);
	const [signalEnabled, setSignalEnabled] = useState(false);
	// Server-computed "pure contextual" flag. The backend refuses to build a signal
	// audience from one, so the button mirrors that rule rather than approximating it
	// with the user-declared contextualOnly toggle.
	const [isContextual, setIsContextual] = useState(false);
	const [modifierConfiguration, setModifierConfiguration] = useState([]);
	// Per-publisher default modifier settings (e.g. default lookback), keyed by modifier option value
	const [modifierDefaults, setModifierDefaults] = useState({});
	const [criteriaConfiguration, setCriteriaConfiguration] = useState([]);
	const [customTaxonomies, setCustomTaxonomies] = useState([]);
	const [agenticBuilderEnabled, setAgenticBuilderEnabled] = useState(false);
	const [customUserDataConfiguration, setCustomUserDataConfiguration] = useState({ integrations: [] });
	// In create-from-generation, the audience targets the generation's silo (passed
	// as createSiloId) rather than the active silo, so the cohort is created in the
	// silo it was designed for. Falls back to the active silo otherwise.
	const [tagId, setTagId] = useState(() =>
		createSiloId != null ? String(createSiloId) : localStorage.getItem("activeSilo"),
	);
	const [crossSiloName, setCrossSiloName] = useState(null);
	const mountedSiloRef = useRef(localStorage.getItem("activeSilo"));

	// Resolve the cross-silo banner: point tagId at the given silo and, if it
	// differs from the active silo, look up its name (reuses the same pattern as
	// the cross-silo cohort-load path below).
	const targetSiloForCreate = (sId) => {
		if (sId == null) return;
		setTagId(String(sId));
		const active = localStorage.getItem("activeSilo");
		if (String(sId) === active) {
			setCrossSiloName(null);
			return;
		}
		getTag(String(sId))
			.then((tag) => setCrossSiloName(tag?.data?.name || `Silo ${sId}`))
			.catch(() => setCrossSiloName(`Silo ${sId}`));
	};
	const [subdomain, setSubdomain] = useState(null);

	// UI state
	const [loading, setLoading] = useState(false);
	const [toast, setToast] = useState(null);
	const [compactHeader, setCompactHeader] = useState(false);
	const [collapsedCards, setCollapsedCards] = useState(new Set());
	const [destinationsModalOpen, setDestinationsModalOpen] = useState(false);
	const [contextualSwitchModalOpen, setContextualSwitchModalOpen] = useState(false);
	const [incompatibleCriteriaList, setIncompatibleCriteriaList] = useState([]);
	const [sidebarExpanded, setSidebarExpanded] = useState(false);
	// AI creator mode is URL-addressable via the `ai` query param so the embedded
	// creator is bookmarkable / refresh-durable (ASB-3052): ?ai=<generationId>
	// reopens a prior generation's suggested-audience list, ?ai=new starts fresh.
	const [searchParams, setSearchParams] = useSearchParams();
	const aiParam = searchParams.get("ai");
	const aiCreatorMode = aiParam != null;
	const embeddedGenId = aiParam && aiParam !== "new" ? aiParam : null;
	const openAiCreator = (genId) => {
		const next = new URLSearchParams(searchParams);
		next.set("ai", genId || "new");
		setSearchParams(next);
	};
	const closeAiCreator = () => {
		const next = new URLSearchParams(searchParams);
		next.delete("ai");
		setSearchParams(next);
	};
	// The generation a selected audience came from, so "Back to suggested
	// audiences" can return to its list. Embedded sets this when an audience is
	// picked; standalone receives it from router state via fromGenerationId.
	const [lastGenerationId, setLastGenerationId] = useState(null);
	const backGenId = lastGenerationId || fromGenerationId;
	const handleBackToSuggestions = () => {
		if (lastGenerationId) {
			openAiCreator(lastGenerationId); // embedded: reopen the creator in place
		} else if (fromGenerationId) {
			navigate(`/ai-audience-explorer-v2/results/${fromGenerationId}`); // standalone
		}
	};
	const [aiPrompt, setAiPrompt] = useState("");
	const [sidebarBottom, setSidebarBottom] = useState(0);

	// Metrics state
	const [metrics, setMetrics] = useState({ uniqueUsers: null, pageViews: null });
	const [metricsOutdated, setMetricsOutdated] = useState(false);
	const [lookback, setLookback] = useState(30);
	const [lookbackDropdownOpen, setLookbackDropdownOpen] = useState(false);
	const [sizingLoading, setSizingLoading] = useState(false);
	const [logicDropdownOpen, setLogicDropdownOpen] = useState(false);
	const [validationAttempted, setValidationAttempted] = useState(false);
	const [lastSizedPayload, setLastSizedPayload] = useState(null);

	// View mode state
	const [activationMethods, setActivationMethods] = useState([]);
	const [isActive, setIsActive] = useState(true);
	const [showCopyModal, setShowCopyModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showArchiveModal, setShowArchiveModal] = useState(false);
	const [showCreateModeledModal, setShowCreateModeledModal] = useState(false);
	const [copyName, setCopyName] = useState("");
	const [modeledName, setModeledName] = useState("");
	const [showCreateSignalModal, setShowCreateSignalModal] = useState(false);
	const [signalName, setSignalName] = useState("");
	const [cohortCid, setCohortCid] = useState(null);
	const [actionLoading, setActionLoading] = useState(false);
	const [isAutoGenerated, setIsAutoGenerated] = useState(false);
	const [originalCompositeConditions, setOriginalCompositeConditions] = useState(null);

	// GAM network selection state
	const [gamNetworkCodes, setGamNetworkCodes] = useState([]);
	const [gamNetworkSelectionMode, setGamNetworkSelectionMode] = useState("all");
	const [gamNetworkSubset, setGamNetworkSubset] = useState([]);

	// Role-based permissions
	const [readOnly, setReadOnly] = useState(false);
	// Publisher Contributor (role 5) can edit content but cannot push/pull destinations. See ASB-3200.
	const [canManageDestinations, setCanManageDestinations] = useState(false);
	// ...nor delete or archive/activate audiences (same permission set). See ASB-3200.
	const [canManageLifecycle, setCanManageLifecycle] = useState(false);

	// Format numbers with compact notation (e.g., 1.2M)
	const formatNumber = (num) => {
		const formatter = Intl.NumberFormat("en-US", { notation: "compact" });
		return formatter.format(num);
	};

	// Check user role for read-only access
	useEffect(() => {
		const session = getSession();
		// Outside the `if`: getSession() returns null for a missing, malformed or
		// expired token, and `readOnly` defaulting to false is the UNSAFE direction —
		// it decides whether Copy is offered (ASB-4023), and Copy creates an audience
		// the API refuses to non-editors. Its two neighbours default to false safely,
		// which is why only this one is hoisted. computeReadOnly(undefined) is true.
		//
		// Defence in depth, not a reachable bug: with no session the page never renders
		// this menu (the header needs cohort data it cannot fetch), so there is no e2e
		// case for it — tests/utilities/permissions.test.js pins the predicate instead.
		setReadOnly(computeReadOnly(session?.role_id));
		if (session) {
			setCanManageDestinations(computeCanManageDestinations(session.role_id));
			setCanManageLifecycle(computeCanManageLifecycle(session.role_id));
		}
	}, []);

	// Reload page if silo changes in another tab
	useEffect(() => {
		const handleStorageChange = () => {
			if (localStorage.getItem("activeSilo") !== mountedSiloRef.current) {
				window.location.reload();
			}
		};
		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [mode]);

	// Set page title
	useEffect(() => {
		const titles = {
			create: "Create New Audience",
			edit: "Edit Audience",
			view: "View Audience",
		};
		const base = titles[mode] || "Audience Builder";
		document.title = audienceName && mode !== "create"
			? `${base} | ${audienceName}`
			: base;
	}, [mode, audienceName]);

	// Enforce transient mode for AMS Contextual flavor
	useEffect(() => {
		if (amsFlavor === "contextual") {
			setMembershipModeSetting("transient");
			setResolvedMembershipMode("transient");
		}
	}, [amsFlavor]);

	// Auto-compute membership mode when criteria or sub-cohort cache changes
	useEffect(() => {
		if (membershipModeSetting === "auto") {
			const computed = inferMembershipModeFromCriteria(
				criteriaGroups,
				(uniqueId) => subCohortCache[uniqueId] || "transient"
			);
			setResolvedMembershipMode(computed);
		}
	}, [criteriaGroups, subCohortCache, membershipModeSetting]);

	// Load cohort data for edit/view modes
	useEffect(() => {
		const fetchCohortData = async () => {
			if ((mode === "edit" || mode === "view") && cohortId && tagId) {
				setLoading(true);
				try {
					const response = await getCohortbyCohortID(cohortId, tagId);
					const cohort = response.data;

					// Cross-silo: if cohort belongs to a different silo, switch internally
					const cohortTagId = cohort.tagId > 0 ? `${cohort.tagId}` : tagId;
					const activeSilo = localStorage.getItem("activeSilo");
					if (cohortTagId !== activeSilo) {
						// Cohort belongs to a different silo than the header's active silo
						try {
							const cohortTag = await getTag(cohortTagId);
							setCrossSiloName(cohortTag.data?.name || `Silo ${cohortTagId}`);
						} catch (e) {
							setCrossSiloName(`Silo ${cohortTagId}`);
						}
						if (cohortTagId !== tagId) {
							setTagId(cohortTagId);
							return;
						}
					} else {
						// Same silo — clear any previous cross-silo banner
						setCrossSiloName(null);
					}

					// Populate basic fields
					setAudienceName(cohort.name || "");
					setDescription(cohort.description || "");
					setLoadedVersion(cohort.version ?? null);
					setMembershipModeSetting(cohort.membership_mode || "auto");
					setResolvedMembershipMode(cohort.resolved_membership_mode || "transient");
					if (cohort.ttl) {
						setTtl(cohort.ttl);
					}

					// Populate criteria from composite_conditions_v2
					if (cohort.composite_conditions_v2) {
						const { criteriaGroupCondition: groupCondition, criteriaGroups: groups } = cohort.composite_conditions_v2;

						setCriteriaGroupCondition(groupCondition || "and");

						// Transform each criteria group from API format to internal format
						const transformedGroups = (groups || []).map((group) =>
							group.map((apiCriteria) => transformCriteriaFromApi(apiCriteria))
						);

						// Ensure at least one group with one empty criteria
						if (transformedGroups.length === 0) {
							setCriteriaGroups([[{ type: null, data: {} }]]);
						} else {
							setCriteriaGroups(transformedGroups);
						}
					}

					// Set metrics if available (only in view mode - edit mode starts with no metrics)
					if (mode === "view" && cohort.metrics?.last30Days) {
						setMetrics({
							uniqueUsers: cohort.metrics.last30Days.uniqueUsers,
							pageViews: cohort.metrics.last30Days.cohortLoads,
						});
					}

					// Set activation methods (used in view mode and to preserve in edit mode)
					if (cohort.activation_methods) {
						setActivationMethods(cohort.activation_methods);
					}

					// Set active status for archive/unarchive toggle
					setIsActive(cohort.active !== false);

					// Store cid for modeled audience creation
					if (cohort.cid) {
						setCohortCid(cohort.cid);
					}

					// Set auto-generated flag and store original composite_conditions
					setIsAutoGenerated(cohort.auto_generated === true);
					if (cohort.auto_generated === true && cohort.composite_conditions) {
						setOriginalCompositeConditions(cohort.composite_conditions);
					}

					// Load GAM network selection settings
					setGamNetworkSelectionMode(cohort.gamNetworkSelectionMode || "all");
					setGamNetworkSubset(cohort.gamNetworkSubset || []);

					// Load contextual-only flag
					setContextualOnly(cohort.contextualOnly === true);
					setIsContextual(cohort.isContextual === true);
				} catch (error) {
					console.error("Failed to fetch cohort:", error);
					setToast({
						message: error.data?.message || error.message || "Failed to load audience",
						status: "Invalid",
						title: "Error",
					});
				} finally {
					setLoading(false);
				}
			}
		};

		fetchCohortData();
	}, [mode, cohortId, tagId]);

	// Fetch tag settings (including dataCenter, amsFlavor, modelingEnabled, defaultTtl)
	useEffect(() => {
		const fetchTagSettings = async () => {
			if (!tagId) return;
			try {
				const tag = await getTag(tagId);
				setSubdomain(tag.data.subdomain ?? null);
				const settings = tag.data.publisherSettings ?? {};
				setDataCenter(settings.dataCenter ?? "us-east-1");
				setAmsFlavor(settings.ams_flavor ?? "full");
				setModelingEnabled(settings.modeling_enabled ?? false);
				setSignalEnabled(settings.signalEnabled ?? false);
				setCustomTaxonomies(settings.custom_taxonomies ?? []);
				setAgenticBuilderEnabled(settings.agenticBuilderEnabled ?? false);
				// Set TTL from publisher settings (defaults to 30 days)
				const publisherDefaultTtl = settings.defaultTtl ?? 30;
				const defaultTtlOption = ttlOptions.find((opt) => opt.value === publisherDefaultTtl) || ttlOptions.find((opt) => opt.value === 30);
				setDefaultTtl(defaultTtlOption);
				setTtl(publisherDefaultTtl);

				// Fetch GAM network codes from tag integrations
				if (tag.data.integrations?.gam?.networkCodes) {
					setGamNetworkCodes(tag.data.integrations.gam.networkCodes);
				}

				// Fetch builder configuration for custom modifiers and criteria
				const builderConfig = await getTagBuilderConfiguration(tagId);
				const config = builderConfig?.configuration?.siteContent ?? {};
				const publisherName = builderConfig?.publisherName ?? "";
				// Add publisherName and tagId to each modifier for display
				const modifiers = (config.modifier ?? []).map((el) => ({
					...el,
					publisherName,
					tagId,
				}));
				setModifierConfiguration(modifiers);
				// Per-publisher default modifier settings (e.g. default lookback for new modifiers)
				setModifierDefaults(builderConfig?.configuration?.defaults?.modifier ?? {});
				// Add publisherName and tagId to each criteria for display
				const criteria = (config.criteria ?? []).map((el) => ({
					...el,
					publisherName,
					tagId,
				}));
				setCriteriaConfiguration(criteria);

				// Load custom user data configuration
				// Get custom integrations from builder config
				const customUserDataConfig = builderConfig?.configuration?.customUserData ?? {};
				const customIntegrations = (customUserDataConfig.integrations ?? []).map((el) => ({
					...el,
					publisherName,
					tagId,
				}));
				setCustomUserDataConfiguration({
					integrations: customIntegrations,
					gamIntegrationEnabled: settings.gamIntegrationEnabled ?? false,
				});
			} catch (error) {
				console.error("Failed to fetch tag settings:", error);
			}
		};
		fetchTagSettings();
	}, [tagId]);

	// Populate form from initialData (passed from AI Audience Explorer V2 via navigation state)
	useEffect(() => {
		if (mode === "create" && initialData) {
			if (initialData.name) setAudienceName(initialData.name);
			if (initialData.description) setDescription(initialData.description);
			if (initialData.criteriaGroupCondition) setCriteriaGroupCondition(initialData.criteriaGroupCondition);
			if (initialData.criteriaGroups?.length > 0) setCriteriaGroups(initialData.criteriaGroups);
		}
	}, []);

	// Create-from-generation (standalone): the audience belongs to the generation's
	// silo — show the cross-silo banner when it differs from the active silo.
	// (tagId is already initialized to createSiloId above.)
	useEffect(() => {
		if (mode === "create" && createSiloId != null) {
			targetSiloForCreate(createSiloId);
		}
	}, [mode, createSiloId]);

	// Compact header on scroll (with hysteresis to prevent flickering)
	useEffect(() => {
		let ticking = false;

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					const compactThreshold = 100; // Enter compact mode above this
					const expandThreshold = 20; // Exit compact mode below this

					setCompactHeader((prev) => {
						if (prev && window.scrollY < expandThreshold) {
							return false;
						} else if (!prev && window.scrollY > compactThreshold) {
							return true;
						}
						return prev;
					});
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Adjust sidebar bottom to avoid overlapping with app footer
	useEffect(() => {
		const updateSidebarBottom = () => {
			const footer = document.querySelector("footer");
			if (footer) {
				const footerRect = footer.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				// If footer is visible in viewport, adjust sidebar bottom
				if (footerRect.top < viewportHeight) {
					setSidebarBottom(viewportHeight - footerRect.top);
				} else {
					setSidebarBottom(0);
				}
			}
		};

		updateSidebarBottom();
		window.addEventListener("scroll", updateSidebarBottom, { passive: true });
		window.addEventListener("resize", updateSidebarBottom, { passive: true });
		return () => {
			window.removeEventListener("scroll", updateSidebarBottom);
			window.removeEventListener("resize", updateSidebarBottom);
		};
	}, []);

	/**
	 * Add a new criteria group (with one empty criteria card)
	 */
	const addCriteriaGroup = () => {
		setCriteriaGroups((prev) => [...prev, [{ type: null, data: {} }]]);
	};

	/**
	 * Remove a criteria group by index
	 */
	const removeCriteriaGroup = (groupIndex) => {
		setCriteriaGroups((prev) => prev.filter((_, idx) => idx !== groupIndex));
	};

	/**
	 * Add an empty criteria card to a specific group
	 */
	const addCriteria = (groupIndex) => {
		setCriteriaGroups((prev) => {
			const newGroups = [...prev];
			newGroups[groupIndex] = [
				...newGroups[groupIndex],
				{ type: null, data: {} },
			];
			return newGroups;
		});
	};

	/**
	 * Set the type of a criteria within a group
	 */
	const setCriteriaType = (groupIndex, criteriaIndex, criteriaType) => {
		setCriteriaGroups((prev) => {
			const newGroups = [...prev];
			newGroups[groupIndex] = [...newGroups[groupIndex]];
			newGroups[groupIndex][criteriaIndex] = {
				...newGroups[groupIndex][criteriaIndex],
				type: criteriaType,
				data: {},
			};
			return newGroups;
		});
	};

	/**
	 * Remove a criteria from a group (always keeps at least one empty criteria card)
	 */
	const removeCriteria = (groupIndex, criteriaIndex) => {
		setCriteriaGroups((prev) => {
			const newGroups = [...prev];
			const filteredGroup = newGroups[groupIndex].filter(
				(_, idx) => idx !== criteriaIndex
			);
			// Ensure there's always at least one criteria card in the group
			newGroups[groupIndex] = filteredGroup.length > 0 ? filteredGroup : [{ type: null, data: {} }];
			return newGroups;
		});
	};

	/**
	 * Toggle collapse state of a criteria card
	 */
	const toggleCriteriaCollapse = (groupIndex, criteriaIndex) => {
		const cardKey = `${groupIndex}-${criteriaIndex}`;
		setCollapsedCards((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(cardKey)) {
				newSet.delete(cardKey);
			} else {
				newSet.add(cardKey);
			}
			return newSet;
		});
	};

	/**
	 * Collapse all criteria cards that have a type selected
	 */
	const collapseAllCriteria = () => {
		const allKeys = new Set();
		criteriaGroups.forEach((group, groupIndex) => {
			group.forEach((criteria, criteriaIndex) => {
				if (criteria.type) {
					allKeys.add(`${groupIndex}-${criteriaIndex}`);
				}
			});
		});
		setCollapsedCards(allKeys);
	};

	/**
	 * Expand all criteria cards
	 */
	const expandAllCriteria = () => {
		setCollapsedCards(new Set());
	};

	/**
	 * Update the data of a criteria within a group
	 */
	const updateCriteria = (groupIndex, criteriaIndex, newData) => {
		setCriteriaGroups((prev) => {
			const newGroups = [...prev];
			newGroups[groupIndex] = [...newGroups[groupIndex]];
			newGroups[groupIndex][criteriaIndex] = {
				...newGroups[groupIndex][criteriaIndex],
				data: newData,
			};
			return newGroups;
		});
	};

	/**
	 * Check if the cohort is valid for saving
	 */
	const isValidCohort = () => {
		const hasName = audienceName?.trim().length > 0;
		const hasValidGroup = criteriaGroups.some(isValidGroup);
		const hasIncomplete = criteriaGroups.some((group) => group.some(isIncompleteCriteria));
		return hasName && hasValidGroup && !hasIncomplete;
	};

	/**
	 * Check if a criteria is partially filled (has type but is invalid)
	 * These need user attention, unlike undefined criteria which are just empty
	 */
	const isIncompleteCriteria = (criteria) => {
		if (!criteria || !criteria.type) return false; // Empty/undefined - not incomplete
		return !isValidCriteria(criteria); // Has type but not valid
	};

	/**
	 * Get criteria groups filtered and transformed for save payload
	 * - Removes undefined criteria (type === null)
	 * - Removes empty groups after filtering
	 * - Transforms each criteria to API format
	 */
	const getFilteredCriteriaGroups = () => {
		return criteriaGroups
			.map((group) => group
				.filter((c) => c.type !== null)
				.map(transformCriteriaForApi)
			)
			.filter((group) => group.length > 0);
	};

	/**
	 * Build a string representation of the current sizing payload for comparison
	 * Used to detect if criteria have changed since last sizing
	 */
	const getCurrentSizingPayload = () => {
		return JSON.stringify({
			lookback,
			criteriaGroupCondition,
			criteriaGroups: getFilteredCriteriaGroups(),
		});
	};

	// Check if metrics are outdated whenever relevant state changes
	// Only show outdated banner if user has sized at least once
	useEffect(() => {
		if (lastSizedPayload !== null) {
			const isOutdated = getCurrentSizingPayload() !== lastSizedPayload;
			setMetricsOutdated(isOutdated);
		}
	}, [criteriaGroups, criteriaGroupCondition, lookback, lastSizedPayload]);

	/**
	 * Get available criteria types based on contextual mode, AMS flavor, and enabled integrations
	 */
	const getAvailableCriteriaTypes = () => {
		const isContextualRestricted = amsFlavor === "contextual" || contextualOnly;
		// Check for both custom integrations and the standard GAM integration
		const hasCustomIntegrations = (customUserDataConfiguration.integrations ?? []).length > 0;
		const hasAnyCustomUserDataIntegrations = hasCustomIntegrations || !!customUserDataConfiguration.gamIntegrationEnabled;
		return Object.values(CRITERIA_TYPES).filter((type) => {
			// Filter by contextual restrictions
			if (isContextualRestricted && !type.contextualAllowed) return false;
			// Hide Custom User Data if no integrations are enabled
			if (type.key === "customUserData" && !hasAnyCustomUserDataIntegrations) return false;
			return true;
		});
	};

	/**
	 * Find incompatible criteria types that exist in the current configuration
	 * Returns an array of display names for incompatible criteria types
	 */
	const findIncompatibleCriteria = () => {
		const incompatibleTypes = new Set();
		criteriaGroups.forEach((group) => {
			group.forEach((criteria) => {
				if (criteria.type === "contentConsumption") {
					incompatibleTypes.add("Content Consumption");
				} else if (criteria.type === "demographics") {
					incompatibleTypes.add("Demographics");
				} else if (criteria.type === "syndicated") {
					incompatibleTypes.add("Syndicated Data Segments");
				} else if (criteria.type === "customUserData") {
					incompatibleTypes.add("Custom User Data");
				}
			});
		});
		return Array.from(incompatibleTypes);
	};

	/**
	 * Handle contextual mode toggle
	 */
	const handleContextualToggle = (checked) => {
		if (checked) {
			const incompatible = findIncompatibleCriteria();
			if (incompatible.length > 0) {
				setIncompatibleCriteriaList(incompatible);
				setContextualSwitchModalOpen(true);
			} else {
				setPreviousMembershipModeSetting(membershipModeSetting);
				setContextualOnly(true);
				setMembershipModeSetting("auto");
				// useEffect will resolve to "transient" since all criteria are contextual
			}
		} else {
			setContextualOnly(false);
			const restored = previousMembershipModeSetting ?? "auto";
			setMembershipModeSetting(restored);
			if (restored !== "auto") {
				setResolvedMembershipMode(restored);
			}
			// If "auto", the useEffect will set resolvedMembershipMode
			setPreviousMembershipModeSetting(null);
		}
	};

	/**
	 * Handle confirmation of switching to contextual-only mode
	 * Removes all incompatible criteria from all groups
	 */
	const handleContextualConfirm = () => {
		setCriteriaGroups((prev) => {
			return prev.map((group) => {
				const filtered = group.filter((criteria) => {
					return !["contentConsumption", "demographics", "syndicated", "customUserData"].includes(criteria.type);
				});
				// Ensure group has at least one empty criteria card
				return filtered.length > 0 ? filtered : [{ type: null, data: {} }];
			});
		});
		setPreviousMembershipModeSetting(membershipModeSetting);
		setContextualOnly(true);
		setMembershipModeSetting("auto");
		// useEffect will resolve to "transient" since incompatible criteria were removed
		setContextualSwitchModalOpen(false);
	};

	/**
	 * Handle estimate size click
	 */
	const handleEstimateSize = async () => {
		setSizingLoading(true);

		// Store the payload being sized - this will trigger useEffect to set metricsOutdated to false
		const currentPayload = getCurrentSizingPayload();
		setLastSizedPayload(currentPayload);

		try {
			const payload = {
				data: [{
					tagId: parseInt(tagId),
					schema_version: "2.0",
					lookback: `${lookback}d`,
					composite_conditions_v2: {
						criteriaGroupCondition,
						criteriaGroups: getFilteredCriteriaGroups(),
					},
				}]
			};

			const response = await getCohortSizing(payload);
			const result = await response.json();

			setMetrics({
				uniqueUsers: result.uniqueUsers,
				pageViews: result.pageViews,
			});

			// Handle status codes from API
			switch (result.status) {
				case "timeout":
					setToast({
						message: "Timeout exceeded when sizing cohort. Please try again later.",
						status: "Invalid",
						title: "Timeout exceeded"
					});
					break;
				case "failed":
					setToast({
						message: "Error encountered when sizing cohort. Please reach out to your ArcSpan representative for assistance.",
						status: "Invalid",
						title: "Error encountered"
					});
					break;
				default:
					break;
			}
		} catch (error) {
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: (error.data?.message || error.message || "Please try again later.");

			setToast({
				message: message,
				status: "Invalid",
				title: errors ? error.data.message : "Error",
			});
		} finally {
			setSizingLoading(false);
		}
	};

	/**
	 * Update composite_conditions with current modifier values from criteriaGroups
	 * Used for auto-generated cohorts to sync v1 format with v2 edits
	 */
	const getUpdatedCompositeConditions = () => {
		if (!originalCompositeConditions) return null;

		// Deep copy the original composite_conditions
		const updated = JSON.parse(JSON.stringify(originalCompositeConditions));

		// Find contentConsumption criteria in criteriaGroups and update siteContent rules
		if (updated.siteContent?.rules) {
			let ruleIndex = 0;
			criteriaGroups.forEach((group) => {
				group.forEach((criteria) => {
					if (criteria.type === "contentConsumption" && criteria.data?.modifier) {
						if (updated.siteContent.rules[ruleIndex]?.modifier) {
							// Update count and lookback from current state
							if (criteria.data.modifier.count != null) {
								updated.siteContent.rules[ruleIndex].modifier.count = criteria.data.modifier.count;
							}
							if (criteria.data.modifier.lookback != null) {
								updated.siteContent.rules[ruleIndex].modifier.lookback = criteria.data.modifier.lookback;
							}
							if (criteria.data.modifier.timeUnit != null) {
								updated.siteContent.rules[ruleIndex].modifier.timeUnit = criteria.data.modifier.timeUnit;
							}
						}
						ruleIndex++;
					}
				});
			});
		}

		return updated;
	};

	/**
	 * Build the API payload for create/update cohort
	 * @param {Array|null} destinations - If null, preserve existing activation methods (for edit mode Save Only)
	 * @param {string|null} networkMode - GAM network selection mode ("all" | "subset")
	 * @param {Array|null} networkSubset - Array of selected GAM network codes
	 */
	const buildSavePayload = (destinations = null, networkMode = null, networkSubset = null, equativPrice = null) => {
		// Determine which activation methods to use:
		// - If destinations is explicitly provided (including empty array), use those
		// - If null, preserve existing activation methods in edit mode, or use empty array in create mode
		const methodsToUse = destinations !== null
			? destinations
			: (mode === "edit" ? activationMethods : []);

		// Format activation_methods as array of objects with id, name, type
		const formattedMethods = methodsToUse.map((d) => ({
			id: parseInt(d.id, 10),
			name: d.name,
			type: d.type,
		}));

		// Determine GAM network settings to use
		const finalNetworkMode = networkMode !== null ? networkMode : gamNetworkSelectionMode;
		const finalNetworkSubset = networkSubset !== null ? networkSubset : gamNetworkSubset;

		return {
			data: [{
				name: audienceName,
				description,
				tagId: parseInt(tagId),
				cohort_type: "composite",
				schema_version: "2.0",
				membership_mode: membershipModeSetting,
				...(resolvedMembershipMode === "persistent" && ttl != null && { ttl }),
				activation_methods: formattedMethods,
				composite_conditions_v2: {
					criteriaGroupCondition,
					criteriaGroups: getFilteredCriteriaGroups(),
				},
				gamNetworkSelectionMode: finalNetworkMode,
				gamNetworkSubset: finalNetworkMode === "subset" ? finalNetworkSubset : [],
				...(equativPrice != null && { equativPrice }),
				contextualOnly,
				...(mode === "edit" && isAutoGenerated && { auto_generated: true }),
				...(mode === "edit" && isAutoGenerated && originalCompositeConditions && { composite_conditions: getUpdatedCompositeConditions() }),
				...(mode === "edit" && loadedVersion != null && { version: loadedVersion }),
			}]
		};
	};

	/**
	 * Handle save (Save Only)
	 */
	const handleSave = async () => {
		setValidationAttempted(true);
		if (!isValidCohort()) return;

		setLoading(true);
		try {
			const payload = buildSavePayload();

			let response;
			if (mode === "create") {
				response = await createCohort(payload);
			} else {
				response = await updateCohort(payload, cohortId, tagId);
			}

			const result = await response.json();
			// Until the view page re-reads, its actions must send the version this save left.
			if (mode === "edit" && result?.version != null) setLoadedVersion(result.version);

			setToast({
				message: result.message || "Audience saved successfully",
				status: "Success",
				title: "Success",
			});

			// Navigate to view page
			const newCohortId = mode === "create" ? result.id : cohortId;
			navigate(`/audience-builder-v2/view/${newCohortId}`);

		} catch (error) {
			if (isConcurrentChange(error)) {
				setToast(concurrentChangeToast(error));
				return;
			}
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: (error.data?.message || error.message || "Failed to save audience");

			setToast({
				message: message,
				status: "Invalid",
				title: errors ? error.data.message : "Error",
			});
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Handle Save & Push - opens destinations modal
	 */
	const handleSaveAndPush = () => {
		setValidationAttempted(true);
		if (!isValidCohort()) return;
		setDestinationsModalOpen(true);
	};

	/**
	 * Handle save with selected destinations
	 */
	const handleSaveWithDestinations = async (selectedDestinations, networkMode, networkSubset, equativPrice) => {
		setDestinationsModalOpen(false);
		setLoading(true);

		// Update GAM network state
		setGamNetworkSelectionMode(networkMode);
		setGamNetworkSubset(networkSubset);

		try {
			const payload = buildSavePayload(selectedDestinations, networkMode, networkSubset, equativPrice);

			let response;
			if (mode === "create") {
				response = await createCohort(payload);
			} else {
				response = await updateCohort(payload, cohortId, tagId);
			}

			const result = await response.json();
			// Until the view page re-reads, its actions must send the version this save left.
			if (mode === "edit" && result?.version != null) setLoadedVersion(result.version);

			setToast({
				message: `Audience saved and pushed to ${selectedDestinations.length} destination${selectedDestinations.length !== 1 ? "s" : ""}`,
				status: "Success",
				title: "Success",
			});

			// Navigate to view page
			const newCohortId = mode === "create" ? result.id : cohortId;
			navigate(`/audience-builder-v2/view/${newCohortId}`);

		} catch (error) {
			if (isConcurrentChange(error)) {
				setToast(concurrentChangeToast(error));
				return;
			}
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: (error.data?.message || error.message || "Failed to save and push audience");

			setToast({
				message: message,
				status: "Invalid",
				title: errors ? error.data.message : "Error",
			});
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Close toast
	 */
	const toastClose = () => {
		setToast(null);
	};

	/**
	 * Handle copy cohort
	 */
	const handleCopy = async () => {
		if (!copyName.trim()) return;

		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(cohortId, tagId);
			const cohortData = response.data;

			// Clone and reset IDs
			const payload = {
				data: [{
					...cohortData,
					cid: null,
					unique_id: null,
					name: copyName,
					auto_generated: false,
					tagId: parseInt(tagId),
					version: undefined,
				}]
			};

			await createCohort(payload);
			setToast({ message: "Audience copied successfully", status: "Success", title: "Success" });
			setShowCopyModal(false);
			setCopyName("");
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to copy audience",
				status: "Invalid",
				title: "Error"
			});
		} finally {
			setActionLoading(false);
		}
	};

	/**
	 * Handle delete cohort
	 */
	const handleDelete = async () => {
		setActionLoading(true);
		try {
			await DeleteCohort([cohortId]);
			setToast({ message: "Audience deleted successfully", status: "Success", title: "Success" });
			setShowDeleteModal(false);
			setTimeout(() => {
				navigate("/audience-manager");
			}, 1500);
		} catch (error) {
			setToast({
				message: error.data?.message || error.message || "Failed to delete audience",
				status: "Invalid",
				title: "Error"
			});
		} finally {
			setActionLoading(false);
		}
	};

	/**
	 * Handle archive/unarchive toggle
	 */
	const handleArchiveToggle = async () => {
		setActionLoading(true);
		try {
			const response = await getCohortbyCohortID(cohortId, tagId);
			const payload = {
				data: [{
					...response.data,
					active: !isActive,
					// The version this page loaded, not the re-read's (ASB-4801).
					...(loadedVersion != null && { version: loadedVersion }),
				}]
			};
			const saved = await (await updateCohort(payload, cohortId, tagId)).json();
			if (saved?.version != null) setLoadedVersion(saved.version);
			setIsActive(!isActive);
			setToast({
				message: `Audience ${isActive ? "archived" : "unarchived"} successfully`,
				status: "Success",
				title: "Success"
			});
			setShowArchiveModal(false);
		} catch (error) {
			if (isConcurrentChange(error)) {
				setShowArchiveModal(false);
				setToast(concurrentChangeToast(error));
				return;
			}
			setToast({
				message: error.data?.message || error.message || "Failed to update audience",
				status: "Invalid",
				title: "Error"
			});
		} finally {
			setActionLoading(false);
		}
	};

	/**
	 * Handle export cohort as CSV
	 */
	const handleExport = () => {
		const csvData = [
			["Date Created", "Unique ID", "Audience Name", "Audience Type", "Unique Users", "Status"],
			[new Date().toISOString().split("T")[0], cohortId, audienceName, "Composite", metrics?.uniqueUsers || 0, isActive ? "Active" : "Archived"]
		];
		const csvContent = csvData.map((row) => row.join(",")).join("\n");
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `cohort_${cohortId}.csv`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setActionsMenuOpen(false);
		setToast({ message: "Export downloaded", status: "Success", title: "Success" });
	};

	/**
	 * Handle update destinations
	 */
	const handleUpdateDestinations = async (selectedDestinations, networkMode, networkSubset, equativPrice) => {
		setDestinationsModalOpen(false);
		setLoading(true);

		// Update GAM network state
		setGamNetworkSelectionMode(networkMode);
		setGamNetworkSubset(networkSubset);

		try {
			const response = await getCohortbyCohortID(cohortId, tagId);
			const payload = {
				data: [{
					...response.data,
					activation_methods: selectedDestinations.map((d) => ({
						id: parseInt(d.id, 10),
						name: d.name,
						type: d.type,
					})),
					gamNetworkSelectionMode: networkMode,
					gamNetworkSubset: networkMode === "subset" ? networkSubset : [],
					...(equativPrice != null && { equativPrice }),
					// The version this page loaded, not the re-read's (ASB-4801).
					...(loadedVersion != null && { version: loadedVersion }),
				}]
			};
			const updateResponse = await (await updateCohort(payload, cohortId, tagId)).json();
			if (updateResponse?.version != null) setLoadedVersion(updateResponse.version);
			if (updateResponse?.dv360SlotError) {
				setToast({
					message: `Destinations updated successfully, but ${updateResponse.dv360SlotError}`,
					status: "Info",
					title: "DV360 slot not assigned",
				});
			} else {
				setToast({
					message: `Destinations updated successfully${formatDv360SlotSuffix(updateResponse?.dv360Slot)}`,
					status: "Success",
					title: "Success",
				});
			}
			// Re-fetch rather than trust selectedDestinations: it's sourced from the generic
			// /activationMethods catalog ({id, name, type, label}), so it never carries the
			// per-destination `details` object (e.g. Equativ's details.equativ.price, DV360's
			// slot). Setting activationMethods from it directly would silently strip that detail
			// for as long as the page stays open, so the next time the Destinations modal opens
			// it prefills as if this were a fresh activation (ASB-4022 review). In its own
			// try/catch, after the success toast above: the destinations update already
			// succeeded by this point, so a refetch failure here must not surface as "Failed to
			// update destinations" (review bot, PR #4577) — worst case is just a stale local
			// mirror until the next full page load, same as before this refetch existed.
			try {
				const refreshed = await getCohortbyCohortID(cohortId, tagId);
				setActivationMethods(refreshed.data.activation_methods ?? []);
			} catch (refreshError) {
				console.error("Failed to refresh activation methods after updating destinations:", refreshError);
			}
		} catch (error) {
			if (isConcurrentChange(error)) {
				setToast(concurrentChangeToast(error));
				return;
			}
			setToast({
				message: error.data?.message || error.message || "Failed to update destinations",
				status: "Invalid",
				title: "Error"
			});
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Handle create signal audience - opens modal
	 */
	const handleCreateSignalAudience = () => {
		setSignalName(audienceName + "_SIG");
		setShowCreateSignalModal(true);
	};

	/**
	 * Handle create signal audience submission
	 */
	const handleCreateSignalSubmit = async () => {
		if (!signalName.trim() || !cohortCid) return;

		setActionLoading(true);
		try {
			const payload = {
				data: [{
					name: signalName,
					tagId: parseInt(tagId),
					cohort_type: "signal",
					signal_conditions: {
						base_cohort_id: cohortCid,
						// 150 = 1.5x the site baseline. The threshold is tuned on the signal
						// audience's own page, where the slider shows live reach for each stop.
						affinity_score: 150
					},
					schema_version: "1.0"
				}]
			};

			const response = await createCohort(payload);
			const result = await response.json();

			// Check for error in response body (API may return errors with 200 status)
			if (result.error || result.errors || (result.message && !result.id)) {
				const errors = result.errors ? Object.entries(result.errors) : false;
				const message = errors
					? errors.map((e) => e[1]).join("\n")
					: (result.message || "Failed to create signal audience");
				setShowCreateSignalModal(false);
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? result.message : "Error"
				});
				return;
			}

			setToast({
				message: `Signal audience "${signalName}" was created.`,
				status: "Success",
				title: "Success"
			});
			setShowCreateSignalModal(false);
			setSignalName("");

			const newCohortId = result.id;
			setTimeout(() => {
				navigate(`/audience-signal/view/${newCohortId}`);
			}, 1500);
		} catch (error) {
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: (error.data?.message || error.message || "Failed to create signal audience");
			setShowCreateSignalModal(false);
			setToast({
				message: message,
				status: "Invalid",
				title: errors ? error.data.message : "Error"
			});
		} finally {
			setActionLoading(false);
		}
	};

	/**
	 * Handle create modeled audience - opens modal
	 */
	const handleCreateModeledAudience = () => {
		setModeledName(audienceName + "_MOD");
		setShowCreateModeledModal(true);
	};

	/**
	 * Handle create modeled audience submission
	 */
	const handleCreateModeledSubmit = async () => {
		if (!modeledName.trim() || !cohortCid) return;

		setActionLoading(true);
		try {
			const payload = {
				data: [{
					name: modeledName,
					tagId: parseInt(tagId),
					cohort_type: "modeled",
					modeled_conditions: {
						composite_cohort_id: cohortCid
					},
					schema_version: "1.0"
				}]
			};

			const response = await createCohort(payload);
			const result = await response.json();

			// Check for error in response body (API may return errors with 200 status)
			if (result.error || result.errors || (result.message && !result.id)) {
				const errors = result.errors ? Object.entries(result.errors) : false;
				const message = errors
					? errors.map((e) => e[1]).join("\n")
					: (result.message || "Failed to create modeled audience");
				setShowCreateModeledModal(false);
				setToast({
					message: message,
					status: "Invalid",
					title: errors ? result.message : "Error"
				});
				return;
			}

			setToast({
				message: `Modeled audience "${modeledName}" is being created.`,
				status: "Success",
				title: "Success"
			});
			setShowCreateModeledModal(false);
			setModeledName("");

			// Navigate to the modeled audience page after a short delay
			const newCohortId = result.id;
			setTimeout(() => {
				navigate(`/audience-modeling/view/${newCohortId}`);
			}, 1500);
		} catch (error) {
			const errors = error.data?.errors ? Object.entries(error.data.errors) : false;
			const message = errors
				? errors.map((e) => e[1]).join("\n")
				: (error.data?.message || error.message || "Failed to create modeled audience");
			setShowCreateModeledModal(false);
			setToast({
				message: message,
				status: "Invalid",
				title: errors ? error.data.message : "Error"
			});
		} finally {
			setActionLoading(false);
		}
	};

	/**
	 * Copy ID to clipboard
	 */
	const copyIdToClipboard = () => {
		navigator.clipboard.writeText(cohortId);
		setToast({ message: "ID copied to clipboard", status: "Success", title: "Copied" });
	};

	// Count shown after a card's title as " (n)"; the cards with a notion of selected items expose it.
	const getCriteriaCount = (criteria) => {
		if (criteria.type === "domains") return criteria.data?.domains?.length || 0;
		const card = {
			technographics: TechnographicsCard,
			demographics: DemographicsCard,
			location: LocationCard,
			subCohorts: SubCohortsCard,
			syndicated: SyndicatedCard,
			customUserData: CustomUserDataCard,
		}[criteria.type];
		return card ? card.getSelectedCount(criteria.data) : 0;
	};

	// View mode's three-dot menu (AudienceHeader renders it and closes it on pick)
	const headerMenu = [
		// `readOnly` is the ROLE (set from the session above), not `isReadOnly` further
		// down, which is `mode === "view"` and therefore true for everyone here. Copy
		// creates an audience via POST /cohorts, which the API allows only for the
		// editor roles, so a read-only user could fill in the modal and get a 403. The
		// Audience Manager has always disabled its Copy button the same way (ASB-3200).
		...(!readOnly
			? [
				{
					label: "Copy",
					onClick: () => {
						setCopyName(audienceName + " (Copy)");
						setShowCopyModal(true);
					},
				},
			]
			: []),
		...(!isAutoGenerated && canManageLifecycle ? [{ label: "Delete", onClick: () => setShowDeleteModal(true) }] : []),
		{ label: "Export", onClick: handleExport },
		...(canManageLifecycle
			? [{ label: isActive ? "Archive" : "Unarchive", onClick: () => setShowArchiveModal(true) }]
			: []),
	];

	// Determine if we're in read-only mode (view)
	const isReadOnly = mode === "view";

	// Criteria locked for auto-generated in edit mode
	const isCriteriaReadOnly = isReadOnly || (mode === "edit" && isAutoGenerated);

	// Membership mode always locked for auto-generated
	const isMembershipModeReadOnly = isReadOnly || isAutoGenerated;

	// Site content modifier editable only in persist mode for auto-generated (edit mode only)
	const isModifierEditable = mode === "edit" && isAutoGenerated && resolvedMembershipMode === "persistent";

	if (aiCreatorMode) {
		return (
			<div className="audience-builder-v2 wrapper ai-creator-mode">
				<div className="ai-creator-header">
					<div className="ai-creator-header-left">
						<img src={sparkleIcon} alt="" className="ai-creator-header-icon" />
						<span className="ai-creator-header-title">ArcSpan AI Audience Creator</span>
					</div>
					<button
						type="button"
						className="ai-creator-exit-btn"
						onClick={closeAiCreator}
					>
						Exit to classic mode
					</button>
				</div>
				<AIAudienceExplorerV2
					generationIdProp={embeddedGenId}
					onSelectAudience={(audienceData, srcGenerationId, srcSiloId) => {
						setAudienceName(audienceData.name);
						setDescription(audienceData.description);
						setCriteriaGroupCondition(audienceData.criteriaGroupCondition);
						setCriteriaGroups(audienceData.criteriaGroups);
						if (srcGenerationId) setLastGenerationId(srcGenerationId);
						// Create the cohort in the generation's silo, not the active one.
						if (srcSiloId != null) targetSiloForCreate(srcSiloId);
						closeAiCreator();
					}}
				/>
			</div>
		);
	}

	return (
		<div className={`audience-builder-v2 wrapper ${sidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"} ${isReadOnly ? "readonly" : ""}`}>
			{/* Sidebar */}
			{agenticBuilderEnabled && !isReadOnly && (
			<div className="builder-sidebar" style={{ bottom: `${sidebarBottom}px` }}>
				{!sidebarExpanded ? (
					<button
						type="button"
						className="sidebar-toggle"
						onClick={() => setSidebarExpanded(true)}
						title="Expand sidebar"
					>
						<img src={sparkleIcon} alt="" className="sidebar-icon" />
					</button>
				) : (
					<div className="sidebar-content">
						<div className="sidebar-header">
							<div className="sidebar-title">
								<img src={sparkleIcon} alt="" className="sidebar-title-icon" />
								<span>ArcSpan AI</span>
							</div>
							<button
								type="button"
								className="sidebar-collapse-btn"
								onClick={() => setSidebarExpanded(false)}
								title="Collapse sidebar"
							>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 4L4 12M4 4L4 12M4 12L12 12" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</button>
						</div>
						<div className="sidebar-cta">
							<h2 className="sidebar-cta-heading">Build an audience in seconds with ArcSpan AI</h2>
							<p className="sidebar-cta-subtitle">AI suggests audiences tailored to you, balancing scale and spend</p>
							<Button
								text="Build with ArcSpan AI"
								type="button"
								style="dark"
								disabled={false}
								loaded={false}
								onClick={() => openAiCreator()}
							/>
							<Button
								text="Build it Myself"
								type="button"
								style="light"
								disabled={false}
								loaded={false}
								onClick={() => setSidebarExpanded(false)}
							/>
						</div>
						{/* Previous sidebar UI — AI prompt editor + "Start over" footer
						<div className="sidebar-header">
							<div className="sidebar-title">
								<img src={sparkleIcon} alt="" className="sidebar-title-icon" />
								<span>ArcSpan AI</span>
							</div>
							<button
								type="button"
								className="sidebar-collapse-btn"
								onClick={() => setSidebarExpanded(false)}
								title="Collapse sidebar"
							>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 4L4 12M4 4L4 12M4 12L12 12" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</button>
						</div>
						<div className="sidebar-prompt-section">
							<p className="sidebar-question">What would you like to change about this audience?</p>
							<textarea
								className="sidebar-textarea"
								placeholder="e.g. broaden the location criteria to include more metropolitan areas"
								value={aiPrompt}
								onChange={(e) => setAiPrompt(e.target.value)}
							/>
							<Button
								text="Update Audience"
								type="button"
								style="dark"
								disabled={false}
								loaded={false}
								onClick={() => {
									// TODO: Handle AI update
									console.log("AI prompt:", aiPrompt);
								}}
							/>
						</div>
						<div className="sidebar-footer">
							<button type="button" className="sidebar-start-over-btn" onClick={() => setAiCreatorMode(true)}>
								<img src={sparkleCircleIcon} alt="" className="start-over-icon" />
								<div className="start-over-text">
									<span className="start-over-title">Start over with AI creator</span>
									<span className="start-over-description">AI suggests audiences tailored to you, balancing scale and spend</span>
								</div>
								<svg className="start-over-chevron" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M1 1L7 7L1 13" stroke="#5B5FC7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</button>
						</div>
						*/}
					</div>
				)}
			</div>
			)}

			{/* Sticky Header + Metrics Container (ASB-4018: shared AudienceHeader + MetricsBar) */}
			<div className="sticky-header-container">
				<AudienceHeader
					compact={compactHeader}
					leading={
						mode === "create" && backGenId ? (
							<button
								type="button"
								className="back-to-suggestions-btn"
								onClick={handleBackToSuggestions}
							>
								← Back to suggestions
							</button>
						) : null
					}
					name={audienceName}
					onNameChange={isReadOnly ? undefined : setAudienceName}
					nameError={validationAttempted && !audienceName?.trim()}
					description={description}
					onDescriptionChange={isReadOnly || (mode === "edit" && isAutoGenerated) ? undefined : setDescription}
					id={mode === "create" ? undefined : cohortId}
					onCopyId={copyIdToClipboard}
					destinationsCount={mode === "create" ? undefined : activationMethods.length}
					onConfigureDestinations={canManageDestinations ? () => setDestinationsModalOpen(true) : undefined}
					menu={isReadOnly ? headerMenu : undefined}
					error={
						!isReadOnly &&
						validationAttempted &&
						(!isValidCohort() || criteriaGroups.some((group) => group.some(isIncompleteCriteria))) ? (
							<>
								{!audienceName?.trim() && <span>Name is required</span>}
								{audienceName?.trim() && (
									!criteriaGroups.some(isValidGroup) ? (
										<span>At least 1 valid criteria is required</span>
									) : criteriaGroups.some((group) => group.some(isIncompleteCriteria)) ? (
										<span>Complete or remove incomplete criteria</span>
									) : null
								)}
							</>
						) : null
					}
					actions={
						isReadOnly ? (
							<>
								{/* Create Signal Audience button. Gated independently of modeling:
								    a signal audience is an affinity lookup, not a trained model, so
								    there is no modelingEnabled or unique-user requirement. A pure
								    contextual audience is ineligible -- scoring it against the
								    content that defines it is tautological, and the API refuses it. */}
								{signalEnabled && !readOnly && (
									<div
										data-tooltip-id="signal-audience-tooltip"
										data-tooltip-content={isContextual ? "Not available for contextual-only audiences" : ""}
									>
										<Tooltip id="signal-audience-tooltip" />
										<Button
											text="Create Signal Audience"
											type="button"
											style="light"
											onClick={handleCreateSignalAudience}
											disabled={isContextual}
										/>
									</div>
								)}

								{/* Create Modeled Audience button */}
								{amsFlavor === "full" && modelingEnabled && !readOnly && (
									<div
										data-tooltip-id="modeled-audience-tooltip"
										data-tooltip-content={metrics.uniqueUsers < 5000 ? "Requires at least 5,000 unique users" : ""}
									>
										<Tooltip id="modeled-audience-tooltip" />
										<Button
											text="Create Modeled Audience"
											type="button"
											style="light"
											onClick={handleCreateModeledAudience}
											disabled={metrics.uniqueUsers == null || metrics.uniqueUsers < 5000}
										/>
									</div>
								)}

								{/* Edit Audience button */}
								{!readOnly && (
									<Button
										text="Edit Audience"
										type="button"
										style="dark"
										onClick={() => navigate(`/audience-builder-v2/edit/${cohortId}`)}
									/>
								)}
							</>
						) : mode === "edit" ? (
							<>
								<Button
									text="Discard Changes"
									type="button"
									style="danger"
									disabled={loading}
									onClick={() => navigate(`/audience-builder-v2/view/${cohortId}`)}
								/>
								<Button
									text="Save Audience"
									type="button"
									style="dark"
									disabled={loading || (validationAttempted && !isValidCohort())}
									onClick={handleSave}
								/>
							</>
						) : (
							<>
								<Button
									text={canManageDestinations ? "Save Only" : "Save"}
									type="button"
									style={canManageDestinations ? "light" : "dark"}
									disabled={loading || (validationAttempted && !isValidCohort())}
									onClick={handleSave}
								/>
								{canManageDestinations && (
									<Button
										text="Save & Push"
										type="button"
										style="dark"
										disabled={loading || (validationAttempted && !isValidCohort())}
										onClick={handleSaveAndPush}
									/>
								)}
							</>
						)
					}
				/>

				{/* Metrics Bar */}
				<MetricsBar
					metrics={[
						{ label: "Unique Users", value: metrics.uniqueUsers != null ? formatNumber(metrics.uniqueUsers) : null },
						{ label: "Page Views", value: metrics.pageViews != null ? formatNumber(metrics.pageViews) : null },
					]}
					loading={sizingLoading}
					outdated={metricsOutdated}
					qualifier={isReadOnly ? "Actual" : undefined}
					qualifierTip={
						isReadOnly
							? "Users and page views in the cohort over the last 30 days"
							: `${lookback}-day estimate based on historical data`
					}
					actions={
						!isReadOnly ? (
							<>
											<div className="lookback-dropdown">
												<button
													type="button"
													className="lookback-trigger"
													onClick={() => setLookbackDropdownOpen(!lookbackDropdownOpen)}
												>
													{lookback}-day lookback
													<img src={chevronDownIcon} alt="" className="lookback-chevron" />
												</button>
												{lookbackDropdownOpen && (
													<div className="lookback-menu">
														<button
															type="button"
															className={`lookback-option ${lookback === 7 ? "selected" : ""}`}
															onClick={() => {
																setLookback(7);
																setLookbackDropdownOpen(false);
															}}
														>
															7-day lookback
														</button>
														<button
															type="button"
															className={`lookback-option ${lookback === 30 ? "selected" : ""}`}
															onClick={() => {
																setLookback(30);
																setLookbackDropdownOpen(false);
															}}
														>
															30-day lookback
														</button>
													</div>
												)}
											</div>
											<button
												type="button"
												className="estimate-btn"
												onClick={handleEstimateSize}
												disabled={sizingLoading}
											>
												<img src={resizeIcon} alt="Estimate" />
												{sizingLoading ? "Estimating..." : "Estimate Size"}
											</button>
							</>
						) : null
					}
				/>
			</div>

			{/* Auto-Generated Warning Banner */}
			{mode === "edit" && isAutoGenerated && (
				<div className="auto-generated-warning">
					<div className="container">
						<div className="container--inner">
							<div className="warning-content">
								<img src={infoIcon} alt="Info" className="warning-icon" />
								<span>
									This is an auto-generated cohort.
									{resolvedMembershipMode === "persistent"
										? " Only name, destinations, TTL, and engagement modifier settings can be edited."
										: " Only name and destinations can be edited."}
								</span>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Cross-Silo Info Banner */}
			{crossSiloName && (
				<div className="cross-silo-banner">
					<div className="container">
						<div className="container--inner">
							<div className="banner-content">
								<img src={infoIcon} alt="Info" className="banner-icon" />
								<span>
									This audience belongs to <strong>{crossSiloName}</strong>.
								</span>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Main Content */}
			<div className="bottom-fold">
				<div className="container">
					<div className="container--inner">
						<div className="criteria-section">
							<div className={`criteria-header ${amsFlavor === "contextual" ? "compact" : ""}`}>
								<h2>Audience Criteria</h2>
								<div className="criteria-header-row">
									{!isReadOnly && !isAutoGenerated && amsFlavor !== "contextual" && (
										<div className="contextual-toggle">
											<span className="contextual-label">Use contextual criteria only</span>
											<ToggleSwitch
												id="contextual-only-toggle"
												label="Contextual Only"
												size="sm"
												checked={contextualOnly}
												onInput={handleContextualToggle}
											/>
											<Tooltip id="contextual-tooltip" />
											<div
												className="contextual-help"
												data-tooltip-id="contextual-tooltip"
												data-tooltip-html="Use only <b>domains</b>, <b>realtime site content</b>, <b>technographics</b>, <b>location</b> and <b>other cohorts</b> criteria"
											>
												<img src={questionIcon} alt="Help" />
											</div>
										</div>
									)}
									{!isReadOnly && !isAutoGenerated && (
									<div className="collapse-expand-links">
										<button type="button" className="link-btn" onClick={collapseAllCriteria}>
											Collapse All
										</button>
										<button type="button" className="link-btn" onClick={expandAllCriteria}>
											Expand All
										</button>
									</div>
								)}
								</div>
							</div>

							<div className="logic-selector">
								<span>Audience must meet</span>
								{isReadOnly || isAutoGenerated ? (
									<strong>{criteriaGroupCondition === "and" ? "ALL" : "ANY"}</strong>
								) : (
									<div className="logic-dropdown">
										<button
											type="button"
											className="logic-trigger"
											onClick={() => setLogicDropdownOpen(!logicDropdownOpen)}
										>
											{criteriaGroupCondition === "and" ? "ALL" : "ANY"}
											<img src={chevronDownIcon} alt="" className="logic-chevron" />
										</button>
										{logicDropdownOpen && (
											<div className="logic-menu">
												<button
													type="button"
													className={`logic-option ${criteriaGroupCondition === "and" ? "selected" : ""}`}
													onClick={() => {
														setCriteriaGroupCondition("and");
														setLogicDropdownOpen(false);
													}}
												>
													ALL
												</button>
												<button
													type="button"
													className={`logic-option ${criteriaGroupCondition === "or" ? "selected" : ""}`}
													onClick={() => {
														setCriteriaGroupCondition("or");
														setLogicDropdownOpen(false);
													}}
												>
													ANY
												</button>
											</div>
										)}
									</div>
								)}
								<span>
									of the following <strong>{criteriaGroups.length}{" "}
									criteria group{criteriaGroups.length !== 1 ? "s" : ""}</strong>
								</span>
							</div>

							{/* Criteria Groups */}
							<div className="criteria-groups">
								{criteriaGroups.map((group, groupIndex) => (
									<React.Fragment key={groupIndex}>
										{groupIndex > 0 && (
											<div className="group-connector">
												<span className="connector-badge">
													{criteriaGroupCondition === "and" ? "AND" : "OR"}
												</span>
												<div className="connector-line" />
											</div>
										)}
										<div className="criteria-group">
											<div className="group-number-container">
												<div className="group-number">{groupIndex + 1}</div>
												<div className="group-separator" />
											</div>
											<div className="group-content">
												<div className="group-criteria">
													{group.map((criteria, criteriaIndex) => (
														<React.Fragment key={criteriaIndex}>
															{criteriaIndex > 0 && (
																<div className="criteria-connector">
																	<span className="connector-badge small">
																		{criteriaGroupCondition === "and" ? "OR" : "AND"}
																	</span>
																	<div className="connector-line" />
																</div>
															)}
															<BuilderCard
																incomplete={validationAttempted && isIncompleteCriteria(criteria)}
																title={criteria.type === null ? undefined : CRITERIA_TYPES[criteria.type]?.label || criteria.type}
																count={criteria.type === null ? undefined : getCriteriaCount(criteria)}
																onRemove={
																	criteria.type !== null && !isReadOnly && !isAutoGenerated
																		? () => removeCriteria(groupIndex, criteriaIndex)
																		: undefined
																}
																removeLabel="Remove criteria"
																collapsed={collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																onToggleCollapse={
																	criteria.type !== null &&
																	!isReadOnly &&
																	(!isAutoGenerated || (criteria.type === "contentConsumption" && isModifierEditable))
																		? () => toggleCriteriaCollapse(groupIndex, criteriaIndex)
																		: undefined
																}
															>
																{criteria.type === null ? (
																	// Empty criteria card (no header): the type picker, plus a remove disc past the group's first card
																	<div className="criteria-type-picker">
																		{!isReadOnly && !isAutoGenerated && (
																			<>
																				<div className="criteria-type-selector">
																					<Select
																						name={`criteria-type-${groupIndex}-${criteriaIndex}`}
																						placeholder="Select..."
																						selected={null}
																						onSubmit={(option) => {
																							if (option) {
																								setCriteriaType(groupIndex, criteriaIndex, option.value);
																							}
																						}}
																						options={getAvailableCriteriaTypes().map((type) => ({
																							value: type.key,
																							label: type.label,
																						}))}
																						required={false}
																						disabled={false}
																						search={false}
																					/>
																				</div>
																				{group.length > 1 && (
																					<BuilderCardRemoveButton
																						size="sm"
																						label="Remove criteria"
																						onClick={() => removeCriteria(groupIndex, criteriaIndex)}
																					/>
																				)}
																			</>
																		)}
																	</div>
																) : (
																	// Criteria card with type selected
																	<>
																		{criteria.type === "domains" && (
																			<DomainsCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																			/>
																		)}
																		{criteria.type === "technographics" && (
																			<TechnographicsCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																			/>
																		)}
																		{criteria.type === "demographics" && (
																			<DemographicsCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				dataCenter={dataCenter}
																			/>
																		)}
																		{criteria.type === "location" && (
																			<LocationCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				cardId={`${groupIndex}-${criteriaIndex}`}
																			/>
																		)}
																		{criteria.type === "subCohorts" && (
																			<SubCohortsCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				tagId={tagId}
																				amsFlavor={amsFlavor}
																				modelingEnabled={modelingEnabled}
																				cardId={`${groupIndex}-${criteriaIndex}`}
																				contextualOnly={contextualOnly}
																				onSubCohortMetadataLoaded={(modeMap) => setSubCohortCache((prev) => ({ ...prev, ...modeMap }))}
																			/>
																		)}
																		{criteria.type === "realtimeSiteContent" && (
																			<RealtimeSiteContentCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				tagId={tagId}
																				criteriaConfiguration={criteriaConfiguration}
																				customTaxonomies={customTaxonomies}
																				cardId={`${groupIndex}-${criteriaIndex}`}
																			/>
																		)}
																		{criteria.type === "contentConsumption" && (
																			<ContentConsumptionCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isModifierEditable ? collapsedCards.has(`${groupIndex}-${criteriaIndex}`) : (isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`))}
																				tagId={tagId}
																				modifierConfiguration={modifierConfiguration}
																				modifierDefaults={modifierDefaults}
																				customTaxonomies={customTaxonomies}
																				cardId={`${groupIndex}-${criteriaIndex}`}
																				isModifierPartiallyEditable={isModifierEditable}
																			/>
																		)}
																		{criteria.type === "syndicated" && (
																			<SyndicatedCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				tagId={tagId}
																				dataCenter={dataCenter}
																				subdomain={subdomain}
																			/>
																		)}
																		{criteria.type === "customUserData" && (
																			<CustomUserDataCard
																				data={criteria.data}
																				onChange={(newData) => updateCriteria(groupIndex, criteriaIndex, newData)}
																				isReadOnly={isCriteriaReadOnly}
																				isCollapsed={isCriteriaReadOnly || collapsedCards.has(`${groupIndex}-${criteriaIndex}`)}
																				tagId={tagId}
																				customUserDataConfiguration={customUserDataConfiguration}
																			/>
																		)}
																		{!["domains", "technographics", "demographics", "location", "subCohorts", "realtimeSiteContent", "contentConsumption", "syndicated", "customUserData"].includes(criteria.type) && !collapsedCards.has(`${groupIndex}-${criteriaIndex}`) && (
																			<p className="placeholder-text">
																				{criteria.type} component will be rendered here
																			</p>
																		)}
																	</>
																)}
															</BuilderCard>
														</React.Fragment>
													))}
												</div>
												{!isReadOnly && !isAutoGenerated && (
													<div className="add-criteria-link">
														<span>{criteriaGroupCondition === "and" ? "Or..." : "And..."}</span>
														<button
															type="button"
															className="link-btn"
															onClick={() => addCriteria(groupIndex)}
														>
															+ Add Criteria
														</button>
													</div>
												)}
											</div>
											{!isReadOnly && !isAutoGenerated && criteriaGroups.length > 1 && (
												<button
													type="button"
													className="remove-group-btn"
													onClick={() => removeCriteriaGroup(groupIndex)}
												>
													<img src={xIcon} alt="Remove group" />
												</button>
											)}
										</div>
									</React.Fragment>
								))}
							</div>

							{!isReadOnly && !isAutoGenerated && (
								<div className="add-group-link">
									<span>{criteriaGroupCondition === "and" ? "And..." : "Or..."}</span>
									<button type="button" className="link-btn" onClick={addCriteriaGroup}>
										+ Add Criteria Group
									</button>
								</div>
							)}
						</div>

						{/* Membership Mode & TTL */}
						{!isReadOnly && amsFlavor !== "contextual" && (
							<div className="advanced-options">
								<Select
									name="membership-mode"
									label="Membership Mode"
									info={
									membershipModeSetting === "auto"
										? `Auto-detected: ${resolvedMembershipMode === "persistent"
											? "Users remain in the cohort for the specified time to live."
											: "Users are re-evaluated on each page load."}`
										: resolvedMembershipMode === "persistent"
											? "Users remain in the cohort for the specified time to live."
											: "Users are re-evaluated on each page load."
								}
									selected={{
									value: membershipModeSetting,
									label: membershipModeSetting === "auto"
										? `Auto (${resolvedMembershipMode === "persistent" ? "Persistent" : "Transient"})`
										: membershipModeSetting === "persistent" ? "Persistent" : "Transient",
								}}
									onSubmit={(option) => {
									setMembershipModeSetting(option.value);
									if (option.value !== "auto") {
										setResolvedMembershipMode(option.value);
									}
								}}
									options={[
										{ value: "auto", label: "Auto" },
										{ value: "persistent", label: "Persistent" },
										{ value: "transient", label: "Transient" },
									]}
									disabled={contextualOnly || isMembershipModeReadOnly}
									required={false}
									search={false}
								/>
								{resolvedMembershipMode === "persistent" && (
									<Select
										name="ttl"
										label="Time to Live"
										info={`Amount of time to keep users in the cohort.${defaultTtl ? ` Defaults to ${defaultTtl.label}.` : ""}`}
										selected={ttlOptions.find((opt) => opt.value === ttl) || ttlOptions[0]}
										onSubmit={(option) => setTtl(option.value)}
										options={ttlOptions}
										required={false}
										disabled={false}
										search={true}
										reset={true}
										defaultValue={defaultTtl}
									/>
								)}
							</div>
						)}

						{isReadOnly && amsFlavor !== "contextual" && (
							<div className="advanced-options readonly">
								<div className="option-item">
									<span className="option-label">Membership Mode:</span>
									<Tooltip id="membership-mode-tooltip" />
									<span
										className="option-value"
										data-tooltip-id="membership-mode-tooltip"
										data-tooltip-content={resolvedMembershipMode === "persistent"
											? "Users remain in the cohort for the specified time to live."
											: "Users are re-evaluated on each page load."}
									>
										{membershipModeSetting === "auto"
											? `Auto (${resolvedMembershipMode === "persistent" ? "Persistent" : "Transient"})`
											: resolvedMembershipMode === "persistent" ? "Persistent" : "Transient"}
									</span>
								</div>
								{resolvedMembershipMode === "persistent" && (
									<div className="option-item">
										<span className="option-label">Time to Live:</span>
										<Tooltip id="ttl-tooltip" />
										<span
											className="option-value"
											data-tooltip-id="ttl-tooltip"
											data-tooltip-content="Amount of time to keep users in the cohort."
										>
											{ttlOptions.find((opt) => opt.value === ttl)?.label || `${ttl} days`}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Loading Overlay */}
			{loading && (
				<div className="loading-overlay">
					<Loader size="lg" />
				</div>
			)}

			{/* Toast */}
			{toast && (
				<Toast
					// A new toast is a new instance: an earlier toast's auto-hide timer must not
					// close a refusal that is meant to stay (ASB-4801).
					key={`${toast.title}|${toast.message}`}
					status={toast.status}
					message={toast.message}
					title={toast.title}
					callback={toastClose}
					autoHide={toast.autoHide ?? true}
					action={toast.action}
				/>
			)}

			{/* Destinations Modal */}
			<DestinationsModal
				isOpen={destinationsModalOpen}
				onClose={() => setDestinationsModalOpen(false)}
				onConfirm={mode === "create" ? handleSaveWithDestinations : handleUpdateDestinations}
				loading={loading}
				saveButtonText={mode === "create" ? "Save & Push" : "Update Destinations"}
				initialSelected={mode === "create" ? [] : activationMethods}
				gamNetworkCodes={gamNetworkCodes}
				initialGamNetworkMode={mode === "create" ? "all" : gamNetworkSelectionMode}
				initialGamNetworkSubset={mode === "create" ? [] : gamNetworkSubset}
			/>

			{/* Contextual Switch Modal */}
			<ContextualSwitchModal
				isOpen={contextualSwitchModalOpen}
				onClose={() => setContextualSwitchModalOpen(false)}
				onConfirm={handleContextualConfirm}
				incompatibleCriteria={incompatibleCriteriaList}
			/>

			{/* Copy Modal */}
			{showCopyModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Copy Audience</h2>
						<p>Enter a name for the new audience:</p>
						<input
							type="text"
							className="copy-name-input"
							value={copyName}
							onChange={(e) => setCopyName(e.target.value)}
							placeholder="New audience name"
							autoFocus
						/>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => {
									setShowCopyModal(false);
									setCopyName("");
								}}
								disabled={actionLoading}
							/>
							<Button
								text="Copy"
								type="button"
								style="dark"
								onClick={handleCopy}
								disabled={!copyName.trim() || actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Delete Modal */}
			{showDeleteModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Delete Audience</h2>
						<p>Are you sure you want to permanently delete this audience?</p>
						<p className="audience-name-display">{audienceName}</p>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => setShowDeleteModal(false)}
								disabled={actionLoading}
							/>
							<Button
								text="Delete"
								type="button"
								style="dark"
								onClick={handleDelete}
								disabled={actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Archive/Unarchive Modal */}
			{showArchiveModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>{isActive ? "Archive" : "Unarchive"} Audience</h2>
						<p>Are you sure you want to {isActive ? "archive" : "unarchive"} this audience?</p>
						<p className="audience-name-display">{audienceName}</p>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => setShowArchiveModal(false)}
								disabled={actionLoading}
							/>
							<Button
								text={isActive ? "Archive" : "Unarchive"}
								type="button"
								style="dark"
								onClick={handleArchiveToggle}
								disabled={actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Create Signal Audience Modal */}
			{showCreateSignalModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Create Signal Audience</h2>
						<p>
							Targets the traffic on pages this audience has a strong affinity for. You can
							tune the affinity threshold after it is created.
						</p>
						<BasicInput
							name="signalName"
							customClasses="copy-name-input"
							required={true}
							disabled={false}
							/* Not `editable`: that adds Basic's click-to-edit pencil, which belongs on a
							   field you reveal for editing (the audience name in the header), not on a
							   modal's primary input, which is typeable the moment it opens. */
							editable={false}
							placeholder="Signal audience name"
							value={signalName}
							onInput={(e) => setSignalName(e.target.value)}
						/>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => {
									setShowCreateSignalModal(false);
									setSignalName("");
								}}
								disabled={actionLoading}
							/>
							<Button
								text="Create"
								type="button"
								style="dark"
								onClick={handleCreateSignalSubmit}
								disabled={!signalName.trim() || !cohortCid || actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Create Modeled Audience Modal */}
			{showCreateModeledModal && (
				<div className="confirmation-modal-overlay">
					<div className="confirmation-modal">
						<h2>Create Modeled Audience</h2>
						<p>Enter a name for the modeled audience:</p>
						<input
							type="text"
							className="copy-name-input"
							value={modeledName}
							onChange={(e) => setModeledName(e.target.value)}
							placeholder="Modeled audience name"
							autoFocus
						/>
						<div className="confirmation-buttons">
							<Button
								text="Cancel"
								type="button"
								style="light"
								onClick={() => {
									setShowCreateModeledModal(false);
									setModeledName("");
								}}
								disabled={actionLoading}
							/>
							<Button
								text="Create"
								type="button"
								style="dark"
								onClick={handleCreateModeledSubmit}
								disabled={!modeledName.trim() || !cohortCid || actionLoading}
								loaded={actionLoading}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

AudienceBuilderV2.propTypes = {
	mode: PropTypes.oneOf(["create", "edit", "view"]),
	cohortId: PropTypes.string,
	initialData: PropTypes.object,
	fromGenerationId: PropTypes.string,
	createSiloId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AudienceBuilderV2;
